import { useCallback, useState } from "react";
import { z } from "zod";

/**
 * Guest session opened by the Group unlock in the embedded ticketing (ADR 0005).
 *
 * The iframe never receives the cross-site session cookie, so the access token
 * returned by the guest authentication is kept here, per event, and presented as a
 * Bearer token when fetching the event. Session storage survives the payment
 * redirect within the tab and dies with it.
 */
const embedGuestSessionSchema = z.object({
  accessToken: z.string().min(1),
  // The email that unlocked the Group tickets: the order must be placed with it.
  email: z.string().min(1),
});

export type EmbedGuestSession = z.infer<typeof embedGuestSessionSchema>;

const storageKey = (eventId: string) => `embed-guest-session:${eventId}`;

// Third-party iframes may be denied storage access: the unlock then only lasts
// for the current render, which is still better than a crash.
export const readEmbedGuestSession = (
  eventId: string
): EmbedGuestSession | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(storageKey(eventId));

    if (!raw) return null;

    const parsed = embedGuestSessionSchema.safeParse(JSON.parse(raw));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

export const writeEmbedGuestSession = (
  eventId: string,
  session: EmbedGuestSession
) => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(storageKey(eventId), JSON.stringify(session));
  } catch {
    // Storage denied: keep the in-memory session only.
  }
};

export const useEmbedGuestSession = (eventId: string) => {
  const [guestSession, setGuestSession] = useState<EmbedGuestSession | null>(
    () => readEmbedGuestSession(eventId)
  );

  const saveGuestSession = useCallback(
    (session: EmbedGuestSession) => {
      writeEmbedGuestSession(eventId, session);
      setGuestSession(session);
    },
    [eventId]
  );

  return { guestSession, saveGuestSession };
};
